import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Card, Col, Container, Form, Row, Spinner } from 'react-bootstrap';
import api from '../../api/apiClient';
import { useAuth } from '../../contexts/AuthContext';
import { redirectByRole } from '../../utils/roleRedirect';

const TwoFactorVerify = () => {
  const navigate = useNavigate();
  const { completeAuthSession } = useAuth();
  const tempToken = useMemo(() => sessionStorage.getItem('twofa_temp_token'), []);
  const userPreview = useMemo(() => {
    const raw = sessionStorage.getItem('twofa_user_preview');
    try {
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  const [otp, setOtp] = useState('');
  const [backupCode, setBackupCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');

    if (!tempToken) {
      setErrorMessage('Your session expired. Please login again.');
      return;
    }

    const normalizedOtp = String(otp || '').replace(/\s+/g, '').trim();
    const normalizedBackup = String(backupCode || '').replace(/\s+/g, '').trim();

    if (!normalizedOtp && !normalizedBackup) {
      setErrorMessage('Enter an OTP or a backup code');
      return;
    }

    try {
      setSubmitting(true);
      const response = await api.auth.verifyOtp({
        tempToken,
        otp: normalizedOtp || undefined,
        backupCode: normalizedBackup || undefined
      });

      if (!response.data?.success) {
        setErrorMessage(response.data?.message || 'Invalid OTP');
        return;
      }

      const tokens = response.data?.data?.tokens;
      const user = response.data?.data?.user;
      if (!tokens?.access_token || !user) {
        setErrorMessage('Invalid response from server');
        return;
      }

      const safeUser = completeAuthSession({
        user,
        tokens,
        message: response.data.message || 'Login successful'
      });

      if (!safeUser) {
        setErrorMessage('Failed to save login session');
        return;
      }

      redirectByRole(safeUser.role, navigate);
    } catch (error) {
      const code = error.response?.data?.code;
      if (code === '2FA_SETUP_REQUIRED') {
        navigate('/2fa/setup', { replace: true });
        return;
      }
      setErrorMessage(error.response?.data?.message || 'Failed to verify OTP');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBackToLogin = () => {
    sessionStorage.removeItem('twofa_temp_token');
    sessionStorage.removeItem('twofa_user_preview');
    navigate('/login', { replace: true });
  };

  return (
    <div className="min-vh-100 d-flex align-items-center brand-app-login-page">
      <Container>
        <Row className="justify-content-center">
          <Col xs={12} md={7} lg={5} xl={4}>
            <Card className="shadow-lg border-0" style={{ borderRadius: '12px' }}>
              <Card.Body className="p-4">
                <h4 className="fw-bold mb-2">OTP Verification</h4>
                <p className="text-muted mb-3" style={{ fontSize: 14 }}>
                  {userPreview?.email ? `Enter the code for ${userPreview.email}` : 'Enter your authenticator code.'}
                </p>

                {errorMessage ? <Alert variant="danger">{errorMessage}</Alert> : null}

                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold" style={{ fontSize: 14 }}>
                      OTP Code
                    </Form.Label>
                    <Form.Control
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      placeholder="123456"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                      disabled={submitting}
                    />
                  </Form.Group>

                  <div className="text-center text-muted mb-2" style={{ fontSize: 13 }}>
                    or
                  </div>

                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold" style={{ fontSize: 14 }}>
                      Backup Code
                    </Form.Label>
                    <Form.Control
                      autoComplete="off"
                      placeholder="XXXXXXXXXX"
                      value={backupCode}
                      onChange={(e) => setBackupCode(e.target.value)}
                      disabled={submitting}
                    />
                  </Form.Group>

                  <Button
                    type="submit"
                    className="w-100 fw-semibold brand-app-login-btn"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <Spinner as="span" animation="border" size="sm" className="me-2" />
                        Verifying...
                      </>
                    ) : (
                      'Verify'
                    )}
                  </Button>
                </Form>

                <Button
                  variant="link"
                  className="w-100 mt-2 text-decoration-none"
                  onClick={handleBackToLogin}
                  disabled={submitting}
                >
                  Back to Login
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default TwoFactorVerify;

